import { useState, useEffect, useRef, useCallback } from "react";

const ICE_SERVERS = {
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
  ],
};

export default function useVoiceCall(socket, roomId, username) {
  const [inCall, setInCall] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [peers, setPeers] = useState([]);

  const localStreamRef = useRef(null);
  const peerConnections = useRef({});
  const inCallRef = useRef(false);

  // ✅ UPSERT HELPER (fixes overwrite bugs)
  const upsertPeer = useCallback((id, updates) => {
    setPeers((prev) => {
      const existing = prev.find((p) => p.id === id);

      if (existing) {
        return prev.map((p) =>
          p.id === id ? { ...p, ...updates } : p
        );
      }

      return [...prev, { id, ...updates }];
    });
  }, []);

  // ─── CREATE PEER CONNECTION ─────────────────────────────
  const createPeerConnection = useCallback(
    (from) => {
      const pc = new RTCPeerConnection(ICE_SERVERS);
      peerConnections.current[from] = pc;

      // Add local tracks
      localStreamRef.current?.getTracks().forEach((track) => {
        pc.addTrack(track, localStreamRef.current);
      });

      // ✅ FIXED: preserve username
      pc.ontrack = (event) => {
        upsertPeer(from, {
          stream: event.streams[0],
        });
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socket.emit("call:ice-candidate", {
            to: from,
            candidate: event.candidate,
          });
        }
      };

      pc.onconnectionstatechange = () => {
        console.log(`[WebRTC] ${from} state:`, pc.connectionState);
      };

      return pc;
    },
    [socket, upsertPeer]
  );

  // ─── JOIN CALL ──────────────────────────────────────────
  const joinCall = useCallback(async () => {
    if (!socket) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      localStreamRef.current = stream;

      inCallRef.current = true;
      setInCall(true);

      socket.emit("call:join", { roomId, username });
    } catch (err) {
      console.error("Mic access denied:", err);
      alert("Microphone access is required for voice calls.");
    }
  }, [socket, roomId, username]);

  // ─── LEAVE CALL ─────────────────────────────────────────
  const leaveCall = useCallback(() => {
    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;

    Object.values(peerConnections.current).forEach((pc) => pc.close());
    peerConnections.current = {};

    setPeers([]);
    inCallRef.current = false;
    setInCall(false);
    setIsMuted(false);

    socket?.emit("call:leave", { roomId });
  }, [socket, roomId]);

  // ─── MUTE ───────────────────────────────────────────────
  const toggleMute = useCallback(() => {
    if (!localStreamRef.current) return;

    localStreamRef.current.getAudioTracks().forEach((track) => {
      track.enabled = !track.enabled;
    });

    setIsMuted((prev) => !prev);
  }, []);

  // ─── SOCKET EVENTS ──────────────────────────────────────
  useEffect(() => {
    if (!socket) return;

    // 🔹 New user joined → send offer
    const handleUserJoined = async ({ from, username: peerUsername }) => {
      if (!inCallRef.current) return;

      console.log("[CALL] User joined:", from);

      const pc = createPeerConnection(from);

      // store username immediately
      upsertPeer(from, {
        username: peerUsername,
        stream: null,
      });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      socket.emit("call:offer", {
        to: from,
        offer,
        username,
      });
    };

    // 🔹 Received offer → send answer
    const handleOffer = async ({ from, offer, username: peerUsername }) => {
      if (!inCallRef.current) return;

      console.log("[CALL] Offer from:", from);

      const pc = createPeerConnection(from);

      upsertPeer(from, {
        username: peerUsername,
        stream: null,
      });

      await pc.setRemoteDescription(new RTCSessionDescription(offer));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socket.emit("call:answer", {
        to: from,
        answer,
        username,
      });
    };

    // 🔹 Received answer
    const handleAnswer = async ({ from, answer, username: peerUsername }) => {
      console.log("[CALL] Answer from:", from);

      const pc = peerConnections.current[from];
      if (pc) {
        await pc.setRemoteDescription(
          new RTCSessionDescription(answer)
        );
      }

      if (peerUsername) {
        upsertPeer(from, { username: peerUsername });
      }
    };

    // 🔹 ICE candidate
    const handleIceCandidate = async ({ from, candidate }) => {
      const pc = peerConnections.current[from];
      if (pc && candidate) {
        try {
          await pc.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (err) {
          console.warn("[ICE ERROR]", err);
        }
      }
    };

    // 🔹 User left
    const handleUserLeft = ({ from }) => {
      console.log("[CALL] User left:", from);

      const pc = peerConnections.current[from];
      if (pc) pc.close();

      delete peerConnections.current[from];

      setPeers((prev) => prev.filter((p) => p.id !== from));
    };

    socket.on("call:user-joined", handleUserJoined);
    socket.on("call:offer", handleOffer);
    socket.on("call:answer", handleAnswer);
    socket.on("call:ice-candidate", handleIceCandidate);
    socket.on("call:user-left", handleUserLeft);

    return () => {
      socket.off("call:user-joined", handleUserJoined);
      socket.off("call:offer", handleOffer);
      socket.off("call:answer", handleAnswer);
      socket.off("call:ice-candidate", handleIceCandidate);
      socket.off("call:user-left", handleUserLeft);
    };
  }, [socket, createPeerConnection, upsertPeer, username]);

  // ─── CLEANUP ────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (inCallRef.current) {
        leaveCall();
      }
    };
  }, [leaveCall]);

  return {
    inCall,
    isMuted,
    peers,
    joinCall,
    leaveCall,
    toggleMute,
  };
}